
import Vue from 'vue';
import { PgyAdSdk } from '@fdfe/marketing-sdk';
import { env, monitor } from '@/common/index';
import { analytics } from '@/common/analytics';
import { pageCids, pageBids, metricPoints } from '@/common/statistics';
import GuideButton from '@/components/guide-button.vue';
import FaqModule from './faq.vue';
import GuideHeaderBg from '@/components/guide-header-bg.vue';
import { runtimeMap } from '@/common/utils';
import animationData from '@/assets/lottie/data.json';
import { OpenPageStyle } from '@/common/enum-type';
import type { IDeductOrder } from '@/common/api-type';

const observes: IntersectionObserver[] = [];

export default Vue.extend({
  name: 'GuideModule',
  components: {
    GuideButton,
    FaqModule,
    GuideHeaderBg,
    LottieModule: () => import('@/components/lottie.vue'),
    PgyAdSdk,
  },
  props: {
    isShowButton: {
      type: Boolean,
      default: false,
    },
    isReportPV: {
      type: Boolean,
      default: false,
    },
    deductOrder: {
      type: Object as () => IDeductOrder,
      default: () => ({} as IDeductOrder),
    },
  },
  data() {
    return {
      isFooterBtnFixed: false,
      defaultLottieOptions: {
        animationData,
      },
      anim: {},
      utmSource: env.utmSource,
      runtime: runtimeMap[env.runtime],
      isShowBannerAd: false,
      imageUrl: env.imageUrl,
      isShowPersonImg: false,
      openPageStyle: env.openPageStyle,
    };
  },
  computed: {
    noButtonDescription(): string {
      return this.isShowButton ? '' : 'margin-bottom: .54rem;';
    },
    isNewFullPageStyle(): boolean {
      return this.openPageStyle === OpenPageStyle.Full;
    },
    firstPayText(): string {
      const payModeList = this.deductOrder.payModeList || [];
      return payModeList[0] ? payModeList[0].payModeText : '';
    },
    isIOS(): boolean {
      return env.isIOS;
    },
  },
  created() {
    if (this.isReportPV) {
      analytics.pv(pageCids.guide);
      monitor.reportMetric(metricPoints.initGuideIndexPage, {
        openPageStyle: env.openPageStyle,
      });
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.isShowButton) {
        this.initHeaderBtnObserver();
      }
      this.initMVReportObserver();
    });
  },
  destroyed() {
    observes.splice(0).forEach((ob) => ob.disconnect());
  },
  methods: {
    initHeaderBtnObserver() {
      const headerBtnObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) {
          this.isFooterBtnFixed = true;
        } else {
          this.isFooterBtnFixed = false;
        }
      });
      headerBtnObserver.observe(this.$refs.headerBtn as Element);
      observes.push(headerBtnObserver);
    },
    /**
     * 利用IntersectionObserver API 检测元素曝光，上报相关指标
     * @See https://developer.mozilla.org/zh-CN/docs/Web/API/IntersectionObserver
     */
    initMVReportObserver() {
      // 需要检测曝光的元素ID
      const ids = [
        'guideWhatIsBlock',
        'guideHowToUseBlock',
        'faqBlock',
      ] as const;
      type PageBidIdx = (typeof ids)[number];

      let idView = ['guideWhatIsBlock', 'guideHowToUseBlock', 'faqBlock'];
      // 新样式仅有FAQ
      if (this.isNewFullPageStyle) {
        idView = ['faqBlock'];
      }
      // 元素与视窗交叉区域超过70%就进行上报，因只上报一次，所以同时解除对元素的观察
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = entry.target.id as PageBidIdx;
              analytics.mv(pageBids[id]);
              observer.unobserve(this.$refs[id] as Element);
            }
          });
        },
        {
          threshold: 0.7,
        }
      );
      // 观察对应元素
      idView.forEach((id) => observer.observe(this.$refs[id] as Element));
      // 保存observer的已用，在页面销毁的时候断开oberser监听，避免内存泄露
      observes.push(observer);
    },
    onFaqChange(index: number, item: any) {
      analytics.click(pageBids.faqClick, {
        index,
        title: item.question,
      });
    },
    /**
     * @description: 处理动画
     * @param {*} anim
     * @return {*}
     */
    handleAnimation(anim: any) {
      this.anim = anim;
    },
    /**
     * @description: 拿到鲸湾组件的返回后再显示人物图片，避免人物图片页面跳动
     * @param {*} isShow
     * @return {*}
     */
    onBannerAdVisibleChange(isShow: boolean) {
      this.isShowBannerAd = !!isShow;
      // 延迟显示人物图片
      setTimeout(() => {
        this.isShowPersonImg = true;
      }, 500);
    },
    /**
     * @description: 打开扣款顺序介绍页面
     * @return {*}
     */
    openDeductionOrderHandler() {
      analytics.click(pageBids.halfDeductionOrderClick);
      this.$emit('openDeductionOrderHandler', true);
    },
  },
});
